import tshirtMockup from "@/assets/merch/tshirt-women-mockup.jpeg";
import tshirtMenMockup from "@/assets/merch/tshirt-men-mockup.jpeg";
import hoodieMockup from "@/assets/merch/hoodie-front-mockup.jpeg";
import hoodieLifestyleMockup from "@/assets/merch/hoodie-lifestyle-mockup.jpeg";
import posterAsset from "@/assets/merch/poster-2026.webp.asset.json";
import apronMockup from "@/assets/merch/apron-mockup.jpeg";
import apronNavy1 from "@/assets/merch/apron-navy-1.jpeg";
import apronNavy2 from "@/assets/merch/apron-navy-2.jpeg";
import apronRed1 from "@/assets/merch/apron-red-1.jpeg";
import apronRed2 from "@/assets/merch/apron-red-2.jpeg";
import apronRope1 from "@/assets/merch/apron-rope-1.jpeg";
import { formatCurrency, type SupportedCurrency } from "@/lib/currencyDetection";
const posterMockup = posterAsset.url;

// ── Conversion helpers ──────────────────────────────────────────────

export const MERCH_CONVERSION_RATES: Record<SupportedCurrency, number> = {
  DKK: 1,
  EUR: 0.134,
  USD: 0.145,
};

export function convertPrice(dkkAmount: number, currency: SupportedCurrency): number {
  if (currency === "DKK") return dkkAmount;
  const raw = dkkAmount * MERCH_CONVERSION_RATES[currency];
  return Math.ceil(raw) - 0.01;
}

export function displayPrice(dkkAmount: number, currency: SupportedCurrency): string {
  return formatCurrency(convertPrice(dkkAmount, currency), currency);
}

// ── Colors ──────────────────────────────────────────────────────────

export const COLORS = ["White", "Black", "Pacific"] as const;
export type MerchColor = (typeof COLORS)[number];

export const COLOR_HEX: Record<MerchColor, string> = {
  White: "#ffffff",
  Black: "#1a1a1a",
  Pacific: "#0077b6",
};

// ── Sizes ───────────────────────────────────────────────────────────

export const TSHIRT_SIZES = ["S", "M", "L", "XL", "2XL"] as const;
export const HOODIE_SIZES = ["S", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL"] as const;
export const HOODIE_WOMEN_SIZES = ["XS", "S", "M", "L", "XL", "2XL", "3XL"] as const;
export const APRON_SIZES = ["One Size"] as const;
export const APRON_COLORS = ["Navy", "Red", "Rope"] as const;
export type ApronColor = (typeof APRON_COLORS)[number];
export const APRON_COLOR_HEX: Record<ApronColor, string> = {
  Navy: "#1b3a5c",
  Red: "#c0392b",
  Rope: "#c4a882",
};

// ── Pricing tables (DKK) ────────────────────────────────────────────

export const TSHIRT_PRICING: Record<string, Record<string, number>> = {
  S:     { White: 259, Black: 259, Pacific: 259 },
  M:     { White: 259, Black: 259, Pacific: 259 },
  L:     { White: 259, Black: 259, Pacific: 259 },
  XL:    { White: 259, Black: 259, Pacific: 259 },
  "2XL": { White: 279, Black: 279, Pacific: 279 },
};

export const HOODIE_PRICING: Record<string, Record<string, number>> = {
  S:     { White: 389, Black: 389, Pacific: 389 },
  M:     { White: 389, Black: 389, Pacific: 389 },
  L:     { White: 389, Black: 389, Pacific: 389 },
  XL:    { White: 389, Black: 389, Pacific: 389 },
  "2XL": { White: 399, Black: 399, Pacific: 399 },
  "3XL": { White: 419, Black: 419, Pacific: 419 },
  "4XL": { White: 419, Black: 419, Pacific: 419 },
  "5XL": { White: 429, Black: 429, Pacific: 429 },
};

export const HOODIE_WOMEN_PRICING: Record<string, Record<string, number>> = {
  XS:    { White: 389, Black: 389, Pacific: 389 },
  S:     { White: 389, Black: 389, Pacific: 389 },
  M:     { White: 389, Black: 389, Pacific: 389 },
  L:     { White: 389, Black: 389, Pacific: 389 },
  XL:    { White: 389, Black: 389, Pacific: 389 },
  "2XL": { White: 399, Black: 399, Pacific: 399 },
  "3XL": { White: 419, Black: 419, Pacific: 419 },
};

export const APRON_PRICING: Record<string, Record<string, number>> = {
  "One Size": { Navy: 359, Red: 359, Rope: 359 },
};

// ── Product definitions ─────────────────────────────────────────────

export interface MerchItem {
  id: string;
  name: string;
  shortName: string;
  basePriceDKK: number;
  tag: string;
  image: string;
  images: string[];
  colorImages?: Record<string, string[]>;
  description: string;
  features: string[];
  hasSizeColor?: boolean;
  variant?: "tshirt" | "hoodie" | "apron";
  /** Per-size pricing overrides from creator store DB. Format: { "S": { "default": 290 }, "2XL": { "default": 320 } } */
  pricingOverrides?: Record<string, Record<string, number>>;
  /** DB-driven sizes array for generic products (e.g. ["S","M","L"]) */
  sizes?: string[];
  /** DB-driven colors array for generic products (e.g. ["Black","White"]) */
  colors?: string[];
  /** DB-driven color hex map */
  colorHex?: Record<string, string>;
  /** Whether to show men/women gender toggle */
  hasGenderToggle?: boolean;
  /** Per-product fulfillment provider name (e.g. "Gelato", "Printful") */
  fulfillmentProvider?: string | null;
}

export const MERCH_ITEMS: MerchItem[] = [
  {
    id: "organic-tshirt",
    name: "ChaosKitchen – Cook the Chaos Organic T-Shirt",
    shortName: "Organic T-Shirt",
    basePriceDKK: 259,
    tag: "Unisex",
    image: tshirtMockup,
    images: [tshirtMockup, tshirtMenMockup],
    description:
      "Show your love for ChaosKitchen with this comfortable organic t-shirt made for everyday wear. Perfect for cooking, streaming, or relaxing while representing the ChaosKitchen community.",
    features: [
      "Medium weight fabric: 5.5 oz/yd² (186 g/m²)",
      "Ideal for DTG printing and embroidery",
      "Classic unisex fit with side seams",
      "100% organic ring-spun combed cotton",
    ],
    hasSizeColor: true,
    variant: "tshirt",
    pricingOverrides: TSHIRT_PRICING,
  },
  {
    id: "premium-hoodie",
    name: "ChaosKitchen – Cook the Chaos Premium Hoodie",
    shortName: "Premium Hoodie",
    basePriceDKK: 389,
    tag: "Unisex",
    image: hoodieMockup,
    images: [hoodieMockup, hoodieLifestyleMockup],
    description:
      "Stay warm while cooking up chaos with this premium ChaosKitchen hoodie. Designed for comfort and everyday wear, it's perfect for streamers, food lovers, and anyone who enjoys the energy of ChaosKitchen.",
    features: ["Soft, durable fabric", "Made for relaxing and creating content"],
    hasSizeColor: true,
    variant: "hoodie",
    pricingOverrides: HOODIE_PRICING,
  },
  {
    id: "kitchen-art-poster",
    name: "ChaosKitchen – Cook the Chaos Kitchen Art Poster",
    shortName: "Kitchen Art Poster",
    basePriceDKK: 239,
    tag: "Print · 50×70 cm",
    image: posterMockup,
    images: [posterMockup],
    description:
      "Bring the spirit of ChaosKitchen into your home or studio with this vibrant wall art poster. Printed on museum-quality matte paper with sharp details and rich colors.",
    features: [
      "Museum-quality matte paper",
      "Sharp details and rich colors",
      "Perfect for kitchens and streaming setups",
    ],
  },
  {
    id: "cooking-apron",
    name: "ChaosKitchen – Cook the Chaos Apron",
    shortName: "Cooking Apron",
    basePriceDKK: 359,
    tag: "Unisex · One Size",
    image: apronMockup,
    images: [apronMockup, apronNavy1, apronNavy2, apronRed1, apronRed2, apronRope1],
    colorImages: {
      Navy: [apronNavy1, apronNavy2],
      Red: [apronRed1, apronRed2],
      Rope: [apronRope1],
    },
    description:
      "Cook in style with the official ChaosKitchen apron. Durable, comfortable, and built for real kitchen action — whether you're streaming or just making dinner.",
    features: [
      "Durable poly-cotton blend",
      "Adjustable neck strap",
      "Front pocket for utensils",
      "Machine washable",
    ],
    hasSizeColor: true,
    variant: "apron",
    pricingOverrides: APRON_PRICING,
  },
];

// ── Helpers for getting variant config ───────────────────────────────

export function getVariantConfig(variant: "tshirt" | "hoodie" | "apron") {
  if (variant === "tshirt") {
    return {
      pricing: TSHIRT_PRICING,
      sizes: TSHIRT_SIZES as unknown as string[],
      sizeRange: "S–2XL",
      womenPricing: undefined,
      womenSizes: undefined,
      womenSizeRange: undefined,
    };
  }
  if (variant === "apron") {
    return {
      pricing: APRON_PRICING,
      sizes: APRON_SIZES as unknown as string[],
      sizeRange: "One Size",
      colors: APRON_COLORS as unknown as string[],
      colorHex: APRON_COLOR_HEX as Record<string, string>,
      womenPricing: undefined,
      womenSizes: undefined,
      womenSizeRange: undefined,
    };
  }
  return {
    pricing: HOODIE_PRICING,
    sizes: HOODIE_SIZES as unknown as string[],
    sizeRange: "S–5XL",
    womenPricing: HOODIE_WOMEN_PRICING,
    womenSizes: HOODIE_WOMEN_SIZES as unknown as string[],
    womenSizeRange: "XS–3XL",
  };
}
